import {
  Box,
  Button,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import {
  ArrowUp,
  CaretDown,
  Chats,
  CloudArrowUp,
  CloudCheck,
  CloudSlash,
  Copy,
  Link as LinkIcon, PencilSimple, PencilSimpleLine,
  Trash, Warning, X
} from "@phosphor-icons/react";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDarkMode } from "../../../hooks/useDarkMode";
import { useSidebarData } from "../../../hooks/useSidebarData";
import { FlowService } from "../../../service/FlowService";
import useFlowsStore from "../../../store/storeFlows";
import { useFlowModal } from "./FlowModalContext";
import {
  resolveFlowFolderLocation,
  type FlowModalTarget,
} from "./resolveFlowFolderLocation";
import { useNotificationStore } from "../../../store/storeNotifications";
import { useVersionControlStore } from "../../../store/storeVersionControl";
import { CustomColorPalette } from "../../../theme";
import VersionControlDropdown from "../VersionControlDropdown";
import { IconButton as CustomIconButton } from "./IconButton";

const AgentNameTypography = styled(Typography)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    fontWeight: 500,
    fontSize: "14px",
    color: isDarkMode ? palette.gray[200] : palette.blue[500],
    textTransform: "capitalize",
    maxWidth: "25rem",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  };
});

const TopBarIconButton = styled(IconButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    width: "24px",
    height: "24px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: isDarkMode ? palette.gray[200] : palette.blue[500],
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.blue[50],
      color: isDarkMode ? palette.gray[200] : palette.blue[500],
    },
  };
});

const SaveStatusIconButton = styled(IconButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    width: "24px",
    height: "24px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: isDarkMode ? palette.gray.A100 : palette.neutral[700],
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.neutral[100],
      color: isDarkMode ? palette.gray.A100 : palette.neutral[900],
    },
  };
});

const PublishButton = styled(Button)(({ theme }) => {
  const palette = theme.customColorPalette;
  return {
    height: "32px",
    borderRadius: "8px",
    paddingLeft: "16px",
    paddingRight: "14px",
    paddingTop: "4px",
    paddingBottom: "4px",
    textTransform: "none",
    fontFamily: "Roboto",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    gap: "3px",
    backgroundColor: palette.blue[500],
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: palette.blue[600],
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      boxShadow: "none",
    },
    "&.Mui-disabled .MuiButton-startIcon > div": {
      borderColor: theme.palette.action.disabled,
    },
  };
});

const MenuItemButton = styled(ListItemButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    display: "flex",
    padding: "4px 8px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
    },
  };
});

const DeleteMenuItemButton = styled(ListItemButton)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    display: "flex",
    padding: "4px 8px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : "#fecdd3",
    },
  };
});

const MenuItemIconStyled = styled(ListItemIcon)({
  minWidth: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const menuItemTextSx = (isDarkMode: boolean, palette: CustomColorPalette) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: 1,
  flex: "1 0 0",
  overflow: "hidden",
  color: isDarkMode ? palette.gray[300] : palette.gray[600],
  fontFeatureSettings: "'liga' off, 'clig' off",
  textOverflow: "ellipsis",
  fontFamily: "Roboto",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  margin: 0,
});

const deleteMenuItemTextSx = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: 1,
  flex: "1 0 0",
  overflow: "hidden",
  color: "#b91c1c",
  fontFeatureSettings: "'liga' off, 'clig' off",
  textOverflow: "ellipsis",
  fontFamily: "Roboto",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  margin: 0,
};

interface MenuItemConfig {
  action: () => void;
  icon: PhosphorIcon;
  label: string;
  isDelete?: boolean;
}

interface AgentTopBarProps {
  onChatClick?: () => void;
}

export const AgentTopBar: React.FC<AgentTopBarProps> = ({ onChatClick }) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const saving = useFlowsStore((state) => state.saving);
  const { flows } = useFlowsStore((state) => state);
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );
  const navigate = useNavigate();
  const params = useParams<{ flowId: string }>();
  const flowModal = useFlowModal();
  const { folders: sidebarFolders, spaces } = useSidebarData();

  const [status, setStatus] = useState({
    isOnline: true,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [versionAnchorEl, setVersionAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [warningDismissed, setWarningDismissed] = useState(false);

  const versionControlProps = useVersionControlStore(state => state.versionControlProps);
  const isProductionView = useVersionControlStore(state => state.isProductionView);

  useEffect(() => {
    setWarningDismissed(false);
  }, [isProductionView]);

  const currentVersionName = useMemo(() => {
    if (!versionControlProps) return null;
    if (versionControlProps.selectedVersionId) {
      const found = versionControlProps.versions.find(
        (v) => v.id === versionControlProps.selectedVersionId,
      );
      return found?.name || versionControlProps.liveVersion.name;
    }
    return versionControlProps.liveVersion.name;
  }, [versionControlProps]);

  const activeFlow = flows.find((flow) => flow.id === params.flowId);
  const { addFlow, setFlows } = useFlowsStore((state) => state);

  useEffect(() => {
    const fetchFlowIfNeeded = async () => {
      if (params.flowId && !activeFlow) {
        try {
          const response = await FlowService.getFlow(params.flowId);
          addFlow(response.data);
        } catch (error) {
          console.error("Failed to load flow:", error);
        }
      }
    };

    fetchFlowIfNeeded();
  }, [params.flowId, activeFlow, addFlow]);

  useEffect(() => {
    const handleOnline = () =>
      setStatus((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setStatus((prev) => ({ ...prev, isOnline: false }));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    setStatus((prev) => ({ ...prev, ...saving }));

    if (!saving.finished && saving.status === "error") {
      setNotification({
        type: "error",
        message: "Error saving agent. Please refresh the page.",
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [saving, setNotification]);

  const getTooltipText = () => {
    if (saving.lastSaved && saving.status === "saved") {
      return `Last saved on: ${moment(saving.lastSaved).format("LT")} ${moment(
        saving.lastSaved,
      ).format("L")}`;
    }
    return "";
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    handleClose();
    if (!activeFlow) return;
    const target = activeFlow as FlowModalTarget;
    const { folderId } = resolveFlowFolderLocation(
      target,
      sidebarFolders,
      spaces,
    );
    flowModal.openRenameFlow(target, folderId || "");
  };

  const handleShare = () => {
    handleClose();
    if (!activeFlow) return;
    const target = activeFlow as FlowModalTarget;
    const { folderId } = resolveFlowFolderLocation(
      target,
      sidebarFolders,
      spaces,
    );
    flowModal.openShareFlow(target, folderId || "");
  };

  const handleDuplicate = async () => {
    handleClose();
    if (!activeFlow) return;
    try {
      const target = activeFlow as FlowModalTarget;
      const { folderId } = resolveFlowFolderLocation(
        target,
        sidebarFolders,
        spaces,
      );
      await flowModal.duplicateFlow(target, folderId || "");
      setNotification({
        type: "success",
        message: "Agent duplicated successfully",
      });
    } catch {
      return;
    }
  };

  const handleDelete = async () => {
    handleClose();
    if (!activeFlow) return;

    if (
      !window.confirm(
        "Are you sure you want to delete the agent? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await FlowService.deleteFlow(activeFlow.id);
      window.dispatchEvent(new CustomEvent("sidebar-refresh"));
      setNotification({
        type: "success",
        message: "Agent deleted successfully",
      });
      navigate("/dashboard-beta");
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to delete agent",
      });
    }
  };

  const handleChatClick = () => {
    if (onChatClick) {
      onChatClick();
    } else {
      console.warn("onChatClick not provided to AgentTopBar");
    }
  };

  const textSx = menuItemTextSx(isDarkMode, palette);

  const popoverMenuItems: MenuItemConfig[] = [
    { action: handleRename, icon: PencilSimpleLine, label: "Rename" },
    { action: handleShare, icon: LinkIcon, label: "Share Agent" },
    { action: handleDuplicate, icon: Copy, label: "Duplicate" },
    { action: handleDelete, icon: Trash, label: "Delete Agent", isDelete: true },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: isDarkMode ? palette.gray[900] : palette.gray.A100,
          borderBottom: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]
            }`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "48px",
            paddingX: "16px",
            gap: "16px",
          }}
        >
          {versionControlProps && currentVersionName && (
            <Box sx={{ flexShrink: 0 }}>
              <IconButton
                onClick={(e) => setVersionAnchorEl(e.currentTarget)}
                sx={{
                  height: "32px",
                  borderRadius: "8px",
                  border: isProductionView
                    ? "1px solid #12B76A"
                    : `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[300]}`,
                  backgroundColor: isProductionView
                    ? "rgba(18, 183, 106, 0.08)"
                    : isDarkMode
                      ? palette.gray[800]
                      : palette.gray.A100,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  px: "10px",
                  py: "6px",
                  "&:hover": {
                    backgroundColor: isProductionView
                      ? "rgba(18, 183, 106, 0.16)"
                      : isDarkMode
                        ? palette.gray[700]
                        : palette.gray[100],
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: isProductionView
                      ? "#027A48"
                      : isDarkMode
                        ? palette.gray[200]
                        : palette.gray[700],
                    lineHeight: 1,
                  }}
                >
                  {currentVersionName}
                </Typography>
                {isProductionView && (
                  <>
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: "#12B76A",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        color: "#027A48",
                        lineHeight: 1,
                      }}
                    >
                      Live
                    </Typography>
                  </>
                )}
                <CaretDown
                  size={13}
                  color={isProductionView
                    ? "#027A48"
                    : isDarkMode
                      ? palette.gray[400]
                      : palette.gray[500]}
                />
              </IconButton>
              <Popover
                open={Boolean(versionAnchorEl)}
                anchorEl={versionAnchorEl}
                onClose={() => setVersionAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { mt: 1, overflow: "visible" } } }}
              >
                <VersionControlDropdown
                  isDarkTheme={isDarkMode}
                  selectedVersionId={versionControlProps.selectedVersionId}
                  onSelectVersion={versionControlProps.onSelectVersion}
                  onShowHistory={versionControlProps.onShowHistory}
                  onHideHistory={versionControlProps.onHideHistory}
                  onNavigateToProduction={versionControlProps.onNavigateToProduction}
                  onAddNew={versionControlProps.onAddNew}
                  onPublish={versionControlProps.onPublish}
                  liveVersion={versionControlProps.liveVersion}
                  versions={versionControlProps.versions}
                  historyEntries={versionControlProps.historyEntries}
                  historyVersionName={versionControlProps.historyVersionName}
                  onRestore={versionControlProps.onRestore}
                  showHistory={versionControlProps.showHistory}
                />
              </Popover>
            </Box>
          )}

          {versionControlProps && currentVersionName && (
            <Box
              sx={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                backgroundColor: isDarkMode ? palette.gray[500] : palette.gray[400],
              }}
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AgentNameTypography title={activeFlow?.name}>
              {activeFlow?.name || "Loading..."}
            </AgentNameTypography>
            <TopBarIconButton onClick={handleClick}>
              <CaretDown size={16} />
            </TopBarIconButton>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
            }}
          >
            {!saving.finished && saving.status === "loading" && (
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? palette.gray[400] : palette.gray[600],
                  fontSize: "12px",
                  paddingRight: "8px",
                }}
              >
                Auto saving agent
              </Typography>
            )}

            <Tooltip title={getTooltipText()} arrow placement="bottom">
              <SaveStatusIconButton>
                {!saving.finished && saving.status === "loading" ? (
                  <CloudArrowUp
                    size={16}
                    color={
                      isDarkMode ? palette.gray.A100 : palette.neutral[700]
                    }
                  />
                ) : status.isOnline && saving.status === "saved" ? (
                  <CloudCheck
                    size={16}
                    color={
                      isDarkMode ? palette.gray.A100 : palette.neutral[700]
                    }
                  />
                ) : (
                  <CloudSlash
                    size={16}
                    color={
                      isDarkMode ? palette.gray.A100 : palette.neutral[700]
                    }
                  />
                )}
              </SaveStatusIconButton>
            </Tooltip>

            <CustomIconButton icon={Chats} onClick={handleChatClick} />

            {versionControlProps && (
              <PublishButton
                onClick={versionControlProps.onPublish}
                disabled={isProductionView}
                startIcon={
                  <Box
                    sx={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "999px",
                      border: "1px solid rgba(255,255,255,0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArrowUp size={10} weight="bold" />
                  </Box>
                }
              >
                Publish
              </PublishButton>
            )}
          </Box>
        </Box>
      </Box>

      {isProductionView && !warningDismissed && (
        <Box
          sx={{
            position: "fixed",
            top: "64px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
            width: "400px",
            borderRadius: "16px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
            p: "32px 24px 24px",
          }}
        >
          <IconButton
            onClick={() => setWarningDismissed(true)}
            size="small"
            sx={{
              position: "absolute",
              top: "12px",
              right: "12px",
              color: "#667085",
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            <X size={18} />
          </IconButton>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Warning size={36} color="#F79009" weight="fill" />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                color: "#101828",
                textAlign: "center",
              }}
            >
              You are editing the published version
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#667085",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              You cannot make edits directly to the live version. Please go to editing mode to make changes.
            </Typography>
            {versionControlProps?.onGoToEditing && (
              <Button
                onClick={versionControlProps.onGoToEditing}
                variant="outlined"
                fullWidth
                startIcon={<PencilSimple size={18} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  borderRadius: "8px",
                  py: 1.25,
                  borderColor: "#546BEA",
                  color: "#546BEA",
                  mt: 1,
                  "&:hover": { borderColor: "#3650DE", bgcolor: "rgba(84, 107, 234, 0.04)" },
                }}
              >
                Go to editing mode
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              width: "229px",
              padding: "8px",
              flexDirection: "column",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "8px",
              border: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[300]
                }`,
              backgroundColor: isDarkMode
                ? palette.gray[900]
                : palette.gray.A100,
              marginTop: "8px",
              boxShadow: "none",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
          }}
        >
          {popoverMenuItems.map((item) => {
            const ItemIcon = item.icon;
            if (item.isDelete) {
              return (
                <DeleteMenuItemButton key={item.label} onClick={item.action}>
                  <MenuItemIconStyled>
                    <ItemIcon size={20} color="#b91c1c" />
                  </MenuItemIconStyled>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: deleteMenuItemTextSx }}
                  />
                </DeleteMenuItemButton>
              );
            }
            return (
              <MenuItemButton key={item.label} onClick={item.action}>
                <MenuItemIconStyled>
                  <ItemIcon
                    size={20}
                    color={isDarkMode ? palette.gray[400] : palette.gray[500]}
                  />
                </MenuItemIconStyled>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ sx: textSx }}
                />
              </MenuItemButton>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};
